import { Box, Button, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNotifications } from '@/hooks/use-notifications';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useEditOrganizationMutation } from '@/state/mutations/organizations/use-edit-organization.ts';
import { useGetOrganizationQuery } from '@/state/queries/organizations/use-get-organization.ts';
import { ADDRESS_TYPE } from '@/enums/address.enum';
import { editActiveOrganizationSchemaFactory } from '@/schemas/organizations/edit-active-organization';
import TabsContent from './tabs';
import { useTranslations } from '@/i18n/use-translations';
import StandardModal, { type MenuButtonType as SettingsMenuButtonType } from '@/components/common/standard.modal';
//import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions.ts';
//import { PERMISSIONS } from '@/enums/permissions.enum';
import type { PatchOrganizationDto } from '@/types/dtos/organizations/request/patch-organization.dto';
import type { ServerError } from '@/types/errors/server.error';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import {
  Business,
  CorporateFare,
  ContentCopyOutlined,
  CoPresentOutlined,
  PlayCircleOutline,
  Settings,
} from '@mui/icons-material';
import { useBreakpointUp } from '@/hooks/use-breakpoint';

export type TabMenuButtonType = 'general' | 'billing' | 'invoices' | 'playout' | 'confidence';
export type EditActiveOrganization = z.infer<ReturnType<typeof editActiveOrganizationSchemaFactory>>;

const menuButtons: SettingsMenuButtonType<TabMenuButtonType>[] = [
  { icon: <Settings />, key: 'general', translationKey: 'organization.settingsMenu.general' },
  { icon: <PlayCircleOutline />, key: 'playout', translationKey: 'organization.settingsMenu.playout' },
  { icon: <CoPresentOutlined />, key: 'confidence', translationKey: 'organization.settingsMenu.confidence' },
  { icon: <CorporateFare />, key: 'billing', translationKey: 'organization.settingsMenu.billing' },
  { icon: <ContentCopyOutlined />, key: 'invoices', translationKey: 'organization.settingsMenu.invoices' },
];

export default function EditModal({
  open,
  close,
}: {
  open: boolean;
  close: React.Dispatch<React.SetStateAction<void>>;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const { activeOrganization, setActiveOrganization } = useActiveOrganizationStore();
  const [menuSelected, setMenuSelected] = useState<TabMenuButtonType>('general');
  const [menuOpen, setMenuOpen] = useState(false);
  const isDesktop = useBreakpointUp('sm');

  // const { data: permissions } = usePermissionsQuery();
  // const canUpgradeSubscription = useMemo(
  //   () =>
  //     !!activeOrganization &&
  //     permissions?.organizations?.[activeOrganization.id]?.includes(PERMISSIONS.UPDATE_SUBSCRIPTION_PLAN),
  //   [activeOrganization, permissions],
  // );

  const { data: getOrganizationData, isSuccess: getOrganizationIsSuccess } = useGetOrganizationQuery(
    activeOrganization?.id,
  );

  useEffect(() => {
    if (getOrganizationData && getOrganizationIsSuccess && getOrganizationData.id === activeOrganization?.id) {
      setActiveOrganization(getOrganizationData);
    }
  }, [getOrganizationIsSuccess, getOrganizationData, activeOrganization?.id, setActiveOrganization]);

  const billingAddress = useMemo(
    () => getOrganizationData?.addresses.find((address) => address.type === ADDRESS_TYPE.BILLING),
    [getOrganizationData],
  );

  const editActiveOrganizationSchema = editActiveOrganizationSchemaFactory(t);
  const form = useForm<EditActiveOrganization>({
    mode: 'onChange',
    resolver: zodResolver(editActiveOrganizationSchema),
    values: {
      general: { name: getOrganizationData?.name || '' },
      billingAddress: {
        name: billingAddress?.name,
        address1: billingAddress?.address1,
        address2: billingAddress?.address2,
        city: billingAddress?.city,
        country: billingAddress?.country,
        postCode: billingAddress?.postCode,
        vat: billingAddress?.vat,
      },
    },
  });

  const { mutateAsync: editOrganizationMutation, isPending: editOrganizationPending } = useEditOrganizationMutation();

  const handleMenuSelect = (key: TabMenuButtonType) => {
    if (form.formState.isDirty && !form.formState.isValid) return;
    setMenuSelected(key);
  };

  const handleCancel = () => {
    close();
    form.reset({
      general: { name: getOrganizationData?.name || '' },
      billingAddress: {
        name: billingAddress?.name,
        address1: billingAddress?.address1,
        address2: billingAddress?.address2,
        city: billingAddress?.city,
        country: billingAddress?.country,
        postCode: billingAddress?.postCode,
        vat: billingAddress?.vat,
      },
    });
  };

  const onSubmit = async (data: EditActiveOrganization) => {
    if (!activeOrganization) {
      notification.notifyError(t('organization.updateError'));
      return;
    }
    const updateData: PatchOrganizationDto = { name: data.general.name, addresses: [] };
    if (
      data.billingAddress?.name &&
      data.billingAddress?.address1 &&
      data.billingAddress?.city &&
      data.billingAddress?.country &&
      data.billingAddress?.postCode
    ) {
      updateData.addresses.push({
        name: data.billingAddress?.name,
        address1: data.billingAddress?.address1,
        address2: data.billingAddress?.address2,
        city: data.billingAddress?.city,
        postCode: data.billingAddress?.postCode,
        country: data.billingAddress?.country,
        vat: data.billingAddress?.vat,
        type: ADDRESS_TYPE.BILLING,
      });
    }
    try {
      await editOrganizationMutation({ id: activeOrganization.id, data: updateData });
      setActiveOrganization({ ...activeOrganization, name: updateData.name, addresses: updateData.addresses });
      notification.notifySuccess(t('organization.updateSuccess', { name: updateData.name }));
      close();
    } catch (error) {
      const errorMessage = t('organization.updateError');
      const errorCode = (error as ServerError).errorCode;
      const reason = errorCode ? ` (${t(`error.${errorCode}` as TranslationKey)})` : '';
      notification.notifyError(`${errorMessage}${reason}`);
    }
  };

  if (!getOrganizationIsSuccess || !activeOrganization) return null;

  const topLeftSection = (
    <>
      <ListItem sx={{ minHeight: '48px' }} disablePadding>
        <ListItemButton disableTouchRipple>
          <ListItemIcon sx={{ minWidth: 0 }}>
            <Business />
          </ListItemIcon>
          {(isDesktop || menuOpen) && (
            <ListItemText
              sx={{ textTransform: 'capitalize', mx: 1 }}
              slotProps={{ primary: { fontSize: '.9em' } }}
              primary={activeOrganization?.name}
            />
          )}
        </ListItemButton>
      </ListItem>
    </>
  );

  const footer = (
    <>
      <Button
        onClick={handleCancel}
        variant="outlined"
        size="small"
        sx={{
          'color': 'warning.main',
          'borderColor': 'warning.main',
          '&:hover': { borderColor: 'warning.dark', backgroundColor: 'warning.light', color: 'warning.dark' },
        }}
      >
        {t('common.cancel')}
      </Button>
      <Button
        disabled={!form.formState.isDirty}
        loading={editOrganizationPending}
        onClick={form.handleSubmit(onSubmit)}
        variant="contained"
        size="small"
      >
        {t('common.save')}
      </Button>
    </>
  );

  return (
    <>
      <StandardModal<TabMenuButtonType>
        open={open}
        close={handleCancel}
        title={t('organization.settings')}
        menuButtons={menuButtons}
        menuSelected={menuSelected}
        onMenuOpen={(menuOpen) => setMenuOpen(menuOpen)}
        onMenuSelect={handleMenuSelect}
        topLeftSection={topLeftSection}
        footer={footer}
      >
        <Box component="form" onSubmit={form.handleSubmit(onSubmit)}>
          <TabsContent
            type={menuSelected}
            control={form.control}
            setValue={form.setValue}
            close={close}
            clearErrors={form.clearErrors}
            organizationId={activeOrganization.id}
          />
        </Box>
      </StandardModal>
    </>
  );
}
